import React, { useState, useMemo } from 'react';
import { Calendar as CalendarIcon, LayoutGrid, Search, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
   Pagination,
   PaginationContent,
   PaginationEllipsis,
   PaginationItem,
   PaginationLink,
   PaginationNext,
   PaginationPrevious,
} from "@/components/ui/pagination";
import InspectionsList from './components/InspectionsList';
import InspectionCalendarView from './components/InspectionCalendarView';
import InspectionEventModal from '@/pages/Personnel/components/InspectionDashboardComponents/InspectionEventModal';

import { useInspections } from './hooks/useInspections';
import { Skeleton } from '@/components/ui/skeleton';
// import '@/components/ui/styles/customScrollBar.css';

const ITEMS_PER_PAGE = 5;

const InspectionsPage = () => {
   const [viewMode, setViewMode] = useState('list');
   const [searchQuery, setSearchQuery] = useState('');
   const [statusFilter, setStatusFilter] = useState('all');
   const [currentPage, setCurrentPage] = useState(1);
   const { inspections, loading, error } = useInspections();

   const isChrome = useMemo(() => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isBrave = navigator.brave !== undefined;
      return userAgent.includes('chrome') && !userAgent.includes('edg') && !isBrave;
   }, []);

   const filteredInspections = useMemo(() => {
      return inspections.filter(inspection => {
         const matchesSearch = inspection.applicationNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
            inspection.applicationType.toLowerCase().includes(searchQuery.toLowerCase());
         const matchesStatus = statusFilter === 'all' || inspection.inspectionStatus === statusFilter;
         return matchesSearch && matchesStatus;
      });
   }, [inspections, searchQuery, statusFilter]);

   const paginatedInspections = useMemo(() => {
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      return filteredInspections.slice(startIndex, startIndex + ITEMS_PER_PAGE);
   }, [filteredInspections, currentPage]);

   const totalPages = Math.ceil(filteredInspections.length / ITEMS_PER_PAGE);

   const handlePageChange = (newPage) => {
      setCurrentPage(newPage);
      window.scrollTo(0, 0);
   };

   const handleFilterChange = (newStatus) => {
      setStatusFilter(newStatus);
      setCurrentPage(1);
   };

   const handleSearch = (value) => {
      setSearchQuery(value);
      setCurrentPage(1);
   };

   const StatusFilterComponent = () => {
      if (isChrome) {
         return (
            <select
               value={statusFilter}
               onChange={(e) => handleFilterChange(e.target.value)}
               className="w-[150px] h-10 px-3 py-2 rounded-md border border-input bg-background text-sm"
            >
               <option value="all">All Status</option>
               <option value="Pending">Pending</option>
               <option value="Accepted">Accepted</option>
               <option value="Completed">Completed</option>
               <option value="Reschedule Requested">Reschedule Requested</option>
               <option value="New Schedule Proposed">New Schedule Proposed</option>
            </select>
         );
      }

      return (
         <Select value={statusFilter} onValueChange={handleFilterChange}>
            <SelectTrigger className="w-[150px]">
               <Filter className="h-4 w-4 mr-2" />
               <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
               <SelectItem value="all">All Status</SelectItem>
               <SelectItem value="Pending">Pending</SelectItem>
               <SelectItem value="Accepted">Accepted</SelectItem>
               <SelectItem value="Completed">Completed</SelectItem>
               <SelectItem value="Reschedule Requested">Reschedule Requested</SelectItem>
               <SelectItem value="New Schedule Proposed">New Schedule Proposed</SelectItem>
            </SelectContent>
         </Select>
      );
   };

   if (error) {
      return (
         <div className="container mx-auto p-6 pt-24">
            <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
               <p className="font-medium">Error loading inspections</p>
               <p className="text-sm mt-1">{error.message}</p>
            </div>
         </div>
      );
   }

   return (
      <div className="h-screen mx-auto bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 p-4 pt-20">
         <div className="max-w-[800px] mx-auto space-y-6">
            <h1 className="text-3xl font-bold tracking-tight">My Inspections</h1>

            <div className="flex items-center justify-between gap-3">
               <div className="flex gap-2">
                  <div className="w-[250px]">
                     <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                           placeholder="Search inspections..."
                           value={searchQuery}
                           onChange={(e) => handleSearch(e.target.value)}
                           className="pl-8"
                        />
                     </div>
                  </div>
                  <StatusFilterComponent />
               </div>
               <div className="flex items-center gap-2">
                  <Button
                     variant="outline"
                     onClick={() => setViewMode('list')}
                     className={`flex items-center gap-2 ${viewMode === 'list'
                        ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white'
                        : ''
                        }`}
                  >
                     <LayoutGrid className="h-4 w-4" />
                     <span>List View</span>
                  </Button>
                  <Button
                     variant="outline"
                     onClick={() => setViewMode('calendar')}
                     className={`flex items-center gap-2 ${viewMode === 'calendar'
                        ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white'
                        : ''
                        }`}
                  >
                     <CalendarIcon className="h-4 w-4" />
                     <span>Calendar View</span>
                  </Button>
               </div>
            </div>

            {loading ? (
               <div className="space-y-3">
                  {[1, 2, 3].map((n) => (
                     <div key={n} className="p-6 rounded-lg border bg-card">
                        <Skeleton className="h-6 w-1/3 mb-4" />
                        <div className="space-y-3">
                           <Skeleton className="h-4 w-1/4" />
                           <Skeleton className="h-4 w-1/2" />
                           <Skeleton className="h-4 w-1/3" />
                        </div>
                     </div>
                  ))}
               </div>
            ) : (
               <>
                  {filteredInspections.length === 0 ? (
                     <div className="text-center py-12 bg-muted/50 rounded-lg">
                        <h3 className="text-lg font-medium">No inspections found</h3>
                        <p className="text-muted-foreground mt-1">
                           {searchQuery || statusFilter !== 'all'
                              ? 'Try adjusting your filters'
                              : 'You have no inspections scheduled yet'}
                        </p>
                     </div>
                  ) : (
                     <div className="space-y-3">
                        {viewMode === 'list' ? (
                           <>
                              <div className="flex flex-col applications-container">
                                 <div className="recent-applications-card">
                                    <InspectionsList inspections={paginatedInspections} />
                                 </div>
                                 {totalPages > 1 && (
                                    <div className="flex justify-center py-3 border-t">
                                       <Pagination>
                                          <PaginationContent>
                                             <PaginationItem>
                                                <PaginationPrevious
                                                   onClick={() => handlePageChange(currentPage - 1)}
                                                   disabled={currentPage === 1}
                                                />
                                             </PaginationItem>

                                             {[...Array(totalPages)].map((_, i) => {
                                                const pageNumber = i + 1;
                                                if (
                                                   pageNumber === 1 ||
                                                   pageNumber === totalPages ||
                                                   (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                                                ) {
                                                   return (
                                                      <PaginationItem key={pageNumber}>
                                                         <PaginationLink
                                                            onClick={() => handlePageChange(pageNumber)}
                                                            isActive={currentPage === pageNumber}
                                                         >
                                                            {pageNumber}
                                                         </PaginationLink>
                                                      </PaginationItem>
                                                   );
                                                } else if (
                                                   pageNumber === currentPage - 2 ||
                                                   pageNumber === currentPage + 2
                                                ) {
                                                   return (
                                                      <PaginationItem key={pageNumber}>
                                                         <PaginationEllipsis />
                                                      </PaginationItem>
                                                   );
                                                }
                                                return null;
                                             })}

                                             <PaginationItem>
                                                <PaginationNext
                                                   onClick={() => handlePageChange(currentPage + 1)}
                                                   disabled={currentPage === totalPages}
                                                />
                                             </PaginationItem>
                                          </PaginationContent>
                                       </Pagination>
                                    </div>
                                 )}
                              </div>
                           </>
                        ) : (
                           <div className="rounded-lg overflow-hidden border bg-card recent-applications-card">
                              <InspectionCalendarView inspections={filteredInspections} />
                           </div>
                        )}
                     </div>
                  )}
               </>
            )}
         </div>
      </div>
   );
};

export default InspectionsPage;
